import styled from "@emotion/styled";
import * as Dialog from "@radix-ui/react-dialog";
import { keyframes } from "@emotion/react";
import { COLORS } from "../header/header.styled";
import { tablet } from "../../../media";

/* ── animations ── */
const overlayIn = keyframes`
  from { opacity: 0 }
  to   { opacity: 1 }
`;
const overlayOut = keyframes`
  from { opacity: 1 }
  to   { opacity: 0 }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;
const slideOutLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;

/* ── overlay (fade) ── */
export const Overlay = styled(Dialog.Overlay)({
  position: "fixed",
  inset: 0,
  background: COLORS.scrim50,
  zIndex: 1000,
  '&[data-state="open"]': {
    pointerEvents: "auto" as const,
    animation: `${overlayIn} 200ms ease`,
    animationFillMode: "forwards",
  },
  '&[data-state="closed"]': {
    pointerEvents: "none" as const,
    animation: `${overlayOut} 200ms ease`,
    animationFillMode: "forwards",
  },
});

/* ── side sheet (slide from left) ── */
export const Sheet = styled(Dialog.Content)({
  position: "fixed",
  top: 0,
  left: 0,
  bottom: 0,
  width: "100%",
  boxSizing: "border-box" as const,
  paddingTop: "env(safe-area-inset-top, 0px)",
  background: COLORS.appBg,
  color: COLORS.textPrimary,
  zIndex: 1001,
  display: "flex",
  flexDirection: "column",
  outline: "none",
  willChange: "transform",
  overflow: "hidden",
  minHeight: 0,
  '&[data-state="open"]': {
    pointerEvents: "auto" as const,
    animation: `${slideInLeft} 260ms cubic-bezier(.22,.61,.36,1)`,
    animationFillMode: "forwards",
  },
  '&[data-state="closed"]': {
    pointerEvents: "none" as const,
    animation: `${slideOutLeft} 220ms cubic-bezier(.22,.61,.36,1)`,
    animationFillMode: "forwards",
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    '&[data-state="open"]': { transform: "translateX(0)" },
    '&[data-state="closed"]': { transform: "translateX(-100%)" },
  },
});

export const Header = styled.header`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 16px;
  border-bottom: 1px solid ${COLORS.border};
`;

export const H1 = styled.h1`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    "Helvetica Neue",
    Arial,
    "Noto Sans",
    "Apple Color Emoji",
    "Segoe UI Emoji";
  font-weight: 600;
  font-size: 20px;
  margin: 0;
  padding: 0 16px;
  height: 46px;

  ${tablet`
    display: none;
  `}
`;

export const NavTitle = styled.div`
  display: none;

  ${tablet`
    display: flex;
    align-items: center;
    font-family:
      Inter,
      system-ui,
      -apple-system,
      Segoe UI,
      Roboto,
      sans-serif;
    font-weight: 400;
    font-size: 28px;
    color: ${COLORS.textPrimary};
    padding-bottom: 24px;
  `}
`;

export const TabsBar = styled.nav`
  flex: 0 0 auto;
  position: relative;
  display: flex;
  gap: 8px;
  padding: 0px 0px 16px 16px;
  border-radius: 8px;
  overflow-x: auto;
  &::-webkit-scrollbar {
    display: none;
  }

  ${tablet`
    flex-direction: column;
    width: 200px;
    flex-shrink: 0;
    overflow-x: visible;
    padding: 0;
    gap: 4px;
  `}
`;

export const TabBtn = styled.button`
  flex: 0 0 auto;
  border: 0px solid ${COLORS.border};
  background: ${COLORS.appBg};
  color: ${COLORS.textSecondary};
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 44px;
  &[data-active] {
    background: ${COLORS.surface};
    color: ${COLORS.textPrimary};
    border-color: ${COLORS.outline};
  }
  &[data-active]::after {
    content: "";
    position: absolute;
    left: 1%;
    right: 1%;
    bottom: 0px;
    height: 1px;
    background: ${COLORS.accent};
    border-radius: 0 0 9999px 9999px;
  }

  ${tablet`
    width: 100%;
    text-align: left;
    padding: 12px 16px;

    &[data-active]::after {
      top: 0;
      left: 0;
      bottom: 0;
      right: auto;
      width: 3px;
      height: 100%;
      border-radius: 9999px;
    }
  `}
`;

export const TabsFadeRight = styled.div`
  pointer-events: none;
  flex: 0 0 24px;
  margin-left: -24px;
  background: linear-gradient(to right, transparent, ${COLORS.appBg});

  ${tablet`
    display: none;
  `}
`;

interface PanelProps {
  isNotificationSettings?: boolean;
}

export const Panel = styled.section<PanelProps>`
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 12px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 0 ${COLORS.neutralOverlay} inset;
  outline: 1px #2B2D33 solid;
  outline-offset: -1px;

  ${tablet`
    margin-left: 0;
    margin-right: 0;
  `}
`;

export const Placeholder = styled.div`
  padding: 8px;
`;

export const ContentArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: calc(var(--keyboard-height-final, 0px) + max(16px, env(safe-area-inset-bottom)));

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.3s ease;

  &:hover {
    scrollbar-color: #3a3c44 transparent;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(58, 60, 68, 0);
    border-radius: 4px;
    transition: background 0.3s ease;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: #3a3c44;
  }
  &:hover::-webkit-scrollbar-thumb:hover {
    background: #4a4c54;
  }

  ${tablet`
    flex: 1;
    min-width: 0;
    max-width: 560px;
  `}
`;

/* Desktop two-column layout wrapper: vertical tabs left, content right */
export const SettingsLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;

  ${tablet`
    flex-direction: row;
    max-width: 800px;
    width: 100%;
    gap: 24px;
    padding: 88px 24px 0;
    margin: 0 auto;
  `}
`;
